import { useState, useMemo } from 'react';
import { Plus, Lightbulb, ChevronDown, ChevronUp, Package } from 'lucide-react';
import { Card, CardContent, ErrorState, useToast } from '@mustauto/ui/web';
import { EmptyState } from '@mustauto/ui/web';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@mustauto/ui/web';
import { useAuth } from '@/hooks/useAuth';
import { useInventory } from '@/hooks/useInventory';
import type { InventoryItem } from '@/hooks/useInventory';
import { useInventoryInsights } from '@/hooks/useInventoryInsights';
import type { InsightItem } from '@/hooks/useInventoryInsights';
import { formatReais } from '@/lib/utils';
import { GiroBadge, StockStatusBadge } from './StatusBadge';
import { ItemFormModal } from './ItemFormModal';
import { ItemDrawer } from './ItemDrawer';
import { formatApiError } from '@mustauto/utils';
import { formatStockNumber, formatStockQuantity, stockAfterMovement } from '@mustauto/utils';
import { useCan } from '@/hooks/useCan';

interface ItemsTabProps {
  onCreatePO: (item: InventoryItem) => void;
}

export function ItemsTab({ onCreatePO }: ItemsTabProps) {
  const { toast } = useToast();
  const { isDemo } = useAuth();
  const can = useCan();
  const canSeeCost = can('estoque.custo');
  const [nameFilter, setNameFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'low' | 'zero'>('all');
  const [giroFilter, setGiroFilter] = useState<'all' | 'alto' | 'medio' | 'baixo'>('all');
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editItem, setEditItem] = useState<InventoryItem | null>(null);
  const [drawerItem, setDrawerItem] = useState<InventoryItem | null>(null);
  const [movementItem, setMovementItem] = useState<InventoryItem | null>(null);
  const [movementType, setMovementType] = useState<'in' | 'out' | 'adjustment'>('in');
  const [movementQty, setMovementQty] = useState('');
  const [movementNotes, setMovementNotes] = useState('');
  // Saldo depois de gravar, a mesma conta da API. Campo vazio não é zero: no
  // ajuste, `Number('')` gravaria "contei e não tem nenhum".
  const movementResult =
    movementItem && movementQty.trim() !== ''
      ? stockAfterMovement(movementItem.quantity, movementType, Number(movementQty))
      : null;

  const lowStockOnly = statusFilter === 'low' || statusFilter === 'zero';
  const { items, isLoading, isError, error, refetch, create, update, recordMovement, applySuggestion, isCreating, isMoving, isApplyingSuggestion } =
    useInventory({
      name: nameFilter || undefined,
      category: categoryFilter || undefined,
      lowStockOnly: lowStockOnly || undefined,
    });

  const { insights } = useInventoryInsights();

  const insightsMap = useMemo(() => {
    const map = new Map<string, InsightItem>();
    for (const insight of insights) {
      map.set(insight.itemId, insight);
    }
    return map;
  }, [insights]);

  const suggestions = useMemo(() => insights.filter((i) => i.minSugerido !== null), [insights]);

  const filteredItems = useMemo(() => {
    let result = items;
    if (statusFilter === 'zero') {
      result = result.filter((i) => i.quantity === 0);
    }
    if (giroFilter !== 'all') {
      result = result.filter((i) => {
        const insight = insightsMap.get(i.id);
        return insight?.giroLabel === giroFilter;
      });
    }
    return result;
  }, [items, statusFilter, giroFilter, insightsMap]);

  const categories = useMemo(() => [...new Set(items.map((i) => i.category))].sort(), [items]);

  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Buscar</label>
              <input
                type="text"
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
                placeholder="Nome ou SKU"
                className="rounded-md border border-input bg-background px-3 py-1.5 text-sm w-48"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Categoria</label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="rounded-md border border-input bg-background px-3 py-1.5 text-sm"
              >
                <option value="">Todas</option>
                {categories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as 'all' | 'low' | 'zero')}
                className="rounded-md border border-input bg-background px-3 py-1.5 text-sm"
              >
                <option value="all">Todos</option>
                <option value="low">Estoque baixo</option>
                <option value="zero">Zerados</option>
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Giro</label>
              <select
                value={giroFilter}
                onChange={(e) => setGiroFilter(e.target.value as 'all' | 'alto' | 'medio' | 'baixo')}
                className="rounded-md border border-input bg-background px-3 py-1.5 text-sm"
              >
                <option value="all">Todos</option>
                <option value="alto">Alto</option>
                <option value="medio">Médio</option>
                <option value="baixo">Baixo</option>
              </select>
            </div>
            {(nameFilter || categoryFilter || statusFilter !== 'all' || giroFilter !== 'all') && (
              <button
                onClick={() => {
                  setNameFilter('');
                  setCategoryFilter('');
                  setStatusFilter('all');
                  setGiroFilter('all');
                }}
                className="rounded-md px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Limpar filtros
              </button>
            )}
            {!isDemo && can('estoque.cadastrar') && (
              <div className="ml-auto">
                <button
                  onClick={() => setShowAddModal(true)}
                  className="flex items-center gap-2 rounded-lg bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  <Plus size={16} />
                  Novo item
                </button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* AI Suggestions */}
      {suggestions.length > 0 && (
        <Card className="border-blue-200 bg-blue-50/50 dark:border-blue-900/60 dark:bg-blue-950/20">
          <CardContent className="pt-4 pb-4">
            <button
              onClick={() => setShowSuggestions(!showSuggestions)}
              className="flex w-full items-center gap-2 text-left"
            >
              <Lightbulb size={16} className="text-blue-600 shrink-0 dark:text-blue-400" />
              <span className="text-sm font-semibold text-blue-700 flex-1 dark:text-blue-300">
                {suggestions.length} {suggestions.length === 1 ? 'sugestão' : 'sugestões'} do Motor de Aprendizado
              </span>
              {showSuggestions ? <ChevronUp size={16} className="text-blue-600 dark:text-blue-400" /> : <ChevronDown size={16} className="text-blue-600 dark:text-blue-400" />}
            </button>
            {showSuggestions && (
              <div className="mt-3 space-y-2">
                {suggestions.map((s) => (
                  <div key={s.itemId} className="flex items-center justify-between rounded-lg border border-blue-100 bg-card p-3 dark:border-blue-900/60">
                    <div>
                      <span className="text-sm font-medium">{s.name}</span>
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        Zerou {s.vezesZerou}x em 60d &middot; Min: {s.minAtual} &rarr; {s.minSugerido}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => applySuggestion({ id: s.itemId, newMinQuantity: s.minSugerido! })}
                        disabled={isApplyingSuggestion}
                        className="rounded-md bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 transition-colors disabled:opacity-50"
                      >
                        Aplicar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Items table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      ) : isError && items.length === 0 ? (
        /* O hook devolve `items ?? []`, então a busca que falha chega aqui
           como lista vazia — e "Nenhum item encontrado" com o estoque cheio é
           a resposta errada para quem está decidindo o que comprar. */
        <Card>
          <CardContent>
            <ErrorState title="Não foi possível carregar o estoque" error={error} onRetry={() => refetch()} />
          </CardContent>
        </Card>
      ) : filteredItems.length === 0 ? (
        <Card>
          <CardContent>
            <EmptyState
              icon={<Package size={28} />}
              title="Nenhum item encontrado"
              description="Não encontramos itens no estoque com os filtros atuais. Cadastre um novo item ou ajuste os filtros."
              action={isDemo ? undefined : { label: 'Novo item', onClick: () => setShowAddModal(true) }}
            />
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Item</TableHead>
                <TableHead>Categoria</TableHead>
                <TableHead className="text-right">Qtd / Mín</TableHead>
                {canSeeCost && <TableHead className="text-right">Custo médio</TableHead>}
                <TableHead className="text-right">P. Venda</TableHead>
                {canSeeCost && <TableHead className="text-right">Margem</TableHead>}
                <TableHead className="text-center">Giro</TableHead>
                <TableHead>Local</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredItems.map((item) => {
                const insight = insightsMap.get(item.id);
                const giroLabel = insight?.giroLabel ?? 'baixo';
                const margin = item.salePrice > 0 && item.unitCost != null && item.unitCost > 0
                  ? ((item.salePrice - item.unitCost) / item.salePrice) * 100
                  : 0;
                const rowBg = item.quantity <= 0
                  ? 'bg-red-50/50 dark:bg-red-950/20'
                  : item.lowStock
                    ? 'bg-amber-50/30 dark:bg-amber-950/15'
                    : '';
                return (
                  <TableRow
                    key={item.id}
                    className={`cursor-pointer hover:bg-muted/50 ${rowBg}`}
                    onClick={() => setDrawerItem(item)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div>
                          <div className="font-medium text-sm">{item.name}</div>
                          {item.sku && (
                            <div className="text-xs text-muted-foreground">{item.sku}</div>
                          )}
                        </div>
                        <StockStatusBadge quantity={item.quantity} minQuantity={item.minQuantity} />
                      </div>
                      {item.compatibleMakes && item.compatibleMakes.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {item.compatibleMakes.slice(0, 3).map((m) => (
                            <span key={m} className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                              {m}
                            </span>
                          ))}
                          {item.compatibleMakes.length > 3 && (
                            <span className="text-[10px] text-muted-foreground">+{item.compatibleMakes.length - 3}</span>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{item.category}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={`font-mono ${item.quantity <= 0 ? 'text-red-600 font-bold dark:text-red-400' : item.lowStock ? 'text-amber-600 font-bold dark:text-amber-400' : ''}`}>
                        {formatStockQuantity(item.quantity, item.unit)}
                      </span>
                      <span className="text-muted-foreground font-mono"> / {formatStockNumber(item.minQuantity)}</span>
                    </TableCell>
                    {canSeeCost && (
                      <TableCell className="text-right text-sm">
                        {formatReais(item.avgCost ?? item.unitCost)}
                      </TableCell>
                    )}
                    <TableCell className="text-right text-sm">
                      {formatReais(item.salePrice)}
                    </TableCell>
                    {canSeeCost && <TableCell className="text-right">
                      <span className={`text-sm font-medium ${margin >= 30 ? 'text-emerald-600 dark:text-emerald-400' : margin >= 15 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'}`}>
                        {margin.toFixed(0)}%
                      </span>
                    </TableCell>}
                    <TableCell className="text-center">
                      <GiroBadge label={giroLabel} />
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {item.location ?? '—'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Add/Edit Item Modal */}
      {(showAddModal || editItem) && (
        <ItemFormModal
          item={editItem}
          onSubmit={async (data) => {
            const editing = !!editItem;
            try {
              if (editItem) {
                await update({ id: editItem.id, ...data });
                setEditItem(null);
              } else {
                await create(data);
                setShowAddModal(false);
              }
              toast({ type: 'success', title: editing ? 'Item atualizado' : 'Item criado' });
            } catch (err) {
              toast({
                type: 'error',
                title: editing ? 'Falha ao atualizar item' : 'Falha ao criar item',
                description: formatApiError(err, 'Não foi possível salvar o item. Tente novamente.'),
              });
            }
          }}
          onClose={() => {
            setShowAddModal(false);
            setEditItem(null);
          }}
          isSubmitting={isCreating}
        />
      )}

      {/* Item Drawer */}
      {drawerItem && (
        <ItemDrawer
          item={drawerItem}
          insight={insightsMap.get(drawerItem.id)}
          onClose={() => setDrawerItem(null)}
          onEdit={(item) => {
            setDrawerItem(null);
            setEditItem(item);
          }}
          onMovement={(item) => {
            setDrawerItem(null);
            setMovementItem(item);
            setMovementType('in');
            setMovementQty('');
            setMovementNotes('');
          }}
          onCreatePO={(item) => {
            setDrawerItem(null);
            onCreatePO(item);
          }}
        />
      )}

      {/* Movement Modal */}
      {movementItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4">
            <CardContent className="pt-6">
              <h3 className="text-base font-semibold mb-1">Movimentar estoque: {movementItem.name}</h3>
              <div className="mb-4 text-sm text-muted-foreground">
                Estoque atual:{' '}
                <span className="font-mono font-bold text-foreground">
                  {formatStockQuantity(movementItem.quantity, movementItem.unit)}
                </span>
                <span className="text-xs"> (mín.: {formatStockNumber(movementItem.minQuantity)})</span>
              </div>
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  // A falha antes ficava muda: o modal só não fechava.
                  try {
                    await recordMovement({
                      itemId: movementItem.id,
                      type: movementType,
                      quantity: Number(movementQty),
                      notes: movementNotes || null,
                    });
                    setMovementItem(null);
                    toast({ type: 'success', title: 'Estoque atualizado' });
                  } catch (err) {
                    toast({
                      type: 'error',
                      title: 'Falha ao movimentar estoque',
                      description: formatApiError(err, 'Não foi possível registrar. Tente novamente.'),
                    });
                  }
                }}
                className="space-y-4"
              >
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-muted-foreground">Tipo</label>
                  <select
                    value={movementType}
                    onChange={(e) => setMovementType(e.target.value as 'in' | 'out' | 'adjustment')}
                    className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="in">Entrada (somar)</option>
                    <option value="out">Saída (tirar)</option>
                    <option value="adjustment">Ajuste (corrigir a contagem)</option>
                  </select>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-muted-foreground">
                    {movementType === 'adjustment' ? 'Quantidade que tem na prateleira *' : 'Quantidade *'}
                  </label>
                  <input
                    type="number"
                    value={movementQty}
                    onChange={(e) => setMovementQty(e.target.value)}
                    required
                    // Fracionado: 0,3 L, 30 ml. O passo padrão do navegador é 1.
                    // O ajuste é a contagem, e contar zero é válido.
                    min={movementType === 'adjustment' ? 0 : 0.001}
                    step="0.001"
                    inputMode="decimal"
                    className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                  />
                  {movementResult != null && movementResult < 0 ? (
                    <p className="text-xs text-red-600 dark:text-red-400">
                      Só tem {formatStockQuantity(movementItem.quantity, movementItem.unit)} em estoque.
                    </p>
                  ) : movementResult != null ? (
                    <p className="text-xs text-muted-foreground">
                      Fica com{' '}
                      <span className="font-medium text-foreground">
                        {formatStockQuantity(movementResult, movementItem.unit)}
                      </span>
                    </p>
                  ) : null}
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-muted-foreground">Observação</label>
                  <input
                    type="text"
                    value={movementNotes}
                    onChange={(e) => setMovementNotes(e.target.value)}
                    maxLength={500}
                    className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="submit"
                    disabled={isMoving || movementResult == null || movementResult < 0}
                    className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
                  >
                    {isMoving ? 'Registrando...' : 'Registrar'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setMovementItem(null)}
                    className="rounded-lg border px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
